/**
 * EtapaColuna — uma coluna do board (.kanban__col).
 *
 * - Header: .kanban__col-title com dot (cor = etapa.cor via CSS var --dot)
 *   + nome + .ct (leads_count) ; .kanban__col-actions com "•••".
 * - Menu da coluna (REF-7): Adicionar lead · Filtrar por esta etapa ·
 *   Recolher · Mover esquerda/direita (reordena + persiste) · Ocultar ·
 *   Configurar pipeline.
 * - Coluna recolhida: faixa estreita clicavel que expande de volta.
 * - .kanban__cards: drop target (HTML5 drag, mesma mecanica do Kanbans).
 * - Rodape: botao .kanban__col-add "+ Adicionar".
 *
 * Gates (CRM-02/CRM-23): "Adicionar" exige lead:create e NUNCA aparece na
 * coluna de conversao (tipo FINALIZADO) — lead criado direto nela nao vira
 * ganho, e o backend recusa. Reordenar colunas e "Configurar pipeline" exigem
 * pipeline:configurar.
 *
 * A cor da etapa (etapa.cor) e a UNICA cor hardcoded permitida — e dado de
 * dominio (configurada pelo gestor), nao decoracao. Vai inline na var --dot.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  FiArrowLeft,
  FiArrowRight,
  FiChevronRight,
  FiEyeOff,
  FiFilter,
  FiInbox,
  FiMinimize2,
  FiMoreHorizontal,
  FiPlus,
  FiSettings,
  FiCheck,
  FiCalendar,
} from "react-icons/fi";

import LeadCard from "./LeadCard";
import { MenuItem, MenuList, MenuWrapper } from "./styles";
import { PERIODOS_ENTRADA } from "./periodoEntrada";
import { ehColunaDeConversao } from "./pipelineConfig";

// O que a coluna vazia sugere depende do que a pessoa PODE fazer nela: sugerir
// "+ Adicionar" onde o botao nao existe manda procurar um botao fantasma.
function dicaColunaVazia({ conversao, podeMover, aceitaNovoLead }) {
  if (conversao) {
    return podeMover
      ? "Arraste aqui o lead que virou venda"
      : "Leads convertidos ficam na aba Ganhos";
  }
  if (podeMover && aceitaNovoLead) {
    return (
      <>
        Arraste um card aqui ou use <strong>+ Adicionar</strong>
      </>
    );
  }
  if (podeMover) return "Arraste um card aqui";
  if (aceitaNovoLead) {
    return (
      <>
        Use <strong>+ Adicionar</strong>
      </>
    );
  }
  return null;
}

function EtapaColuna({
  etapa,
  leads,
  podeMover,
  podeAdicionar = false,
  podeConfigurar = false,
  dragLeadId,
  dragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
  onAbrirLead,
  onAdicionar,
  onConfigurar,
  colapsada = false,
  onRecolher,
  onOcultar,
  onFiltrar,
  onMover,
  podeMoverEsq = false,
  podeMoverDir = false,
  periodoEntrada = "",
  onPeriodoEntrada,
  onAbrirIntervalo,
}) {
  const [menuAberto, setMenuAberto] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuAberto) return undefined;
    function onClickFora(event) {
      if (!menuRef.current?.contains(event.target)) setMenuAberto(false);
    }
    function onKey(event) {
      if (event.key === "Escape") setMenuAberto(false);
    }
    document.addEventListener("mousedown", onClickFora);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickFora);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuAberto]);

  const count = leads.length;
  const conversao = ehColunaDeConversao(etapa);
  const aceitaNovoLead = podeAdicionar && !conversao;
  const dica = dicaColunaVazia({ conversao, podeMover, aceitaNovoLead });

  // ----- Coluna recolhida: faixa estreita, clique expande -----
  if (colapsada) {
    return (
      <div
        className="kanban__col kanban__col--colapsada"
        data-tipo={etapa.tipo}
        style={{
          "--dot": etapa.cor || "var(--ink-3)",
          width: 46,
          minWidth: 46,
          flex: "0 0 46px",
          cursor: "pointer",
          alignItems: "center",
        }}
        onClick={() => onRecolher?.(etapa)}
        title={`${etapa.nome} (${count}) — clique para expandir`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onRecolher?.(etapa);
          }
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            paddingTop: 12,
          }}
        >
          <span className="dot" style={{ background: "var(--dot)" }} />
          <span className="ct">{count}</span>
          <span
            style={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
              fontSize: "0.8rem",
              color: "var(--ink-2)",
              marginTop: 6,
              maxHeight: 200,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {etapa.nome}
          </span>
          <FiChevronRight size={14} aria-hidden="true" style={{ color: "var(--ink-3)" }} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="kanban__col"
      data-tipo={etapa.tipo}
      style={{
        "--dot": etapa.cor || "var(--ink-3)",
        ...(dragOver
          ? { outline: "2px dashed var(--accent)", outlineOffset: "-2px" }
          : null),
      }}
      onDragOver={(e) => onDragOver(e, etapa)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, etapa)}
    >
      <div className="kanban__col-head">
        <div className="kanban__col-title">
          <span className="dot" style={{ background: "var(--dot)" }} />
          <span title={etapa.nome}>{etapa.nome}</span>
          <span className="ct">{count}</span>
        </div>
        <div className="kanban__col-actions">
          <MenuWrapper ref={menuRef}>
            <button
              className="btn btn--ghost btn--icon"
              type="button"
              aria-haspopup="menu"
              aria-expanded={menuAberto}
              aria-label={`Ações da etapa ${etapa.nome}`}
              onClick={() => setMenuAberto((v) => !v)}
            >
              <FiMoreHorizontal size={15} aria-hidden="true" />
            </button>
            {menuAberto && (
              <MenuList role="menu">
                {aceitaNovoLead && (
                  <MenuItem role="none">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuAberto(false);
                        onAdicionar(etapa);
                      }}
                    >
                      <FiPlus size={14} aria-hidden="true" /> Adicionar lead
                    </button>
                  </MenuItem>
                )}
                <MenuItem role="none">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuAberto(false);
                      onFiltrar?.(etapa);
                    }}
                  >
                    <FiFilter size={14} aria-hidden="true" /> Filtrar por esta etapa
                  </button>
                </MenuItem>
                {/* Periodo de ENTRADA na etapa. As opcoes ficam aqui (onde o
                    gestor procura, ao lado das outras acoes da coluna), mas o
                    filtro e do QUADRO: comparar colunas exige a mesma janela,
                    e uma janela por coluna seria estado invisivel. O aviso
                    abaixo diz isso em vez de deixar a surpresa acontecer. */}
                {onPeriodoEntrada && (
                  <>
                    <li className="menu-sep" role="separator" />
                    <li className="menu-titulo" aria-hidden="true">
                      Entrou nesta etapa em
                    </li>
                    {PERIODOS_ENTRADA.map((p) => (
                      <MenuItem key={p.id || "todos"} role="none">
                        <button
                          type="button"
                          role="menuitemradio"
                          aria-checked={periodoEntrada === p.id}
                          onClick={() => {
                            setMenuAberto(false);
                            onPeriodoEntrada(p.id);
                          }}
                        >
                          <FiCheck
                            size={14}
                            aria-hidden="true"
                            style={{ opacity: periodoEntrada === p.id ? 1 : 0 }}
                          />
                          {p.label}
                        </button>
                      </MenuItem>
                    ))}
                    {onAbrirIntervalo && (
                      <MenuItem role="none">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setMenuAberto(false);
                            // Os campos De/Ate vivem no seletor da barra (nao
                            // cabem num dropdown de coluna, e sao um so para
                            // o quadro inteiro) — o menu leva ate la.
                            onAbrirIntervalo();
                          }}
                        >
                          <FiCalendar size={14} aria-hidden="true" />
                          Intervalo específico…
                        </button>
                      </MenuItem>
                    )}
                    <li className="menu-nota">Vale para todas as colunas</li>
                  </>
                )}
                <li className="menu-sep" role="separator" />
                <MenuItem role="none">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuAberto(false);
                      onRecolher?.(etapa);
                    }}
                  >
                    <FiMinimize2 size={14} aria-hidden="true" /> Recolher coluna
                  </button>
                </MenuItem>
                {/* Reordenar persiste no backend (etapas-pipeline/reordenar,
                    pipeline:configurar): sem a permissao, o item so levaria
                    a um 403 e a coluna voltaria para o lugar. */}
                {podeConfigurar && (
                  <>
                    <MenuItem role="none">
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!podeMoverEsq}
                        onClick={() => {
                          setMenuAberto(false);
                          onMover?.(etapa, "esq");
                        }}
                      >
                        <FiArrowLeft size={14} aria-hidden="true" /> Mover para a esquerda
                      </button>
                    </MenuItem>
                    <MenuItem role="none">
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!podeMoverDir}
                        onClick={() => {
                          setMenuAberto(false);
                          onMover?.(etapa, "dir");
                        }}
                      >
                        <FiArrowRight size={14} aria-hidden="true" /> Mover para a direita
                      </button>
                    </MenuItem>
                  </>
                )}
                <MenuItem role="none">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuAberto(false);
                      onOcultar?.(etapa);
                    }}
                  >
                    <FiEyeOff size={14} aria-hidden="true" /> Ocultar coluna
                  </button>
                </MenuItem>
                {podeConfigurar && (
                  <MenuItem role="none">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuAberto(false);
                        onConfigurar();
                      }}
                    >
                      <FiSettings size={14} aria-hidden="true" /> Configurar pipeline
                    </button>
                  </MenuItem>
                )}
              </MenuList>
            )}
          </MenuWrapper>
        </div>
      </div>

      <div className="kanban__cards">
        {count === 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "26px 10px",
              margin: "4px 2px",
              border: "1px dashed var(--divider)",
              borderRadius: 10,
              color: "var(--ink-4)",
              textAlign: "center",
            }}
          >
            <FiInbox size={26} aria-hidden="true" style={{ opacity: 0.5 }} />
            <span style={{ fontSize: "0.78rem", fontWeight: 600 }}>Nenhum lead</span>
            {dica && (
              <span style={{ fontSize: "0.68rem", lineHeight: 1.35, opacity: 0.85 }}>
                {dica}
              </span>
            )}
          </div>
        )}
        {leads.map((lead) => (
          <LeadCard
            key={lead.id}
            lead={lead}
            podeMover={podeMover}
            dragging={dragLeadId === lead.id}
            onAbrir={onAbrirLead}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        ))}
      </div>

      {aceitaNovoLead && (
        <button
          className="kanban__col-add"
          type="button"
          onClick={() => onAdicionar(etapa)}
        >
          <FiPlus size={13} aria-hidden="true" /> Adicionar
        </button>
      )}
    </div>
  );
}

export default EtapaColuna;
